import { SelectDocumentModal } from "@/components/features/matter/file-repo/signature-requests/SelectDocumentModal";
import {
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import {
  AddContactFormField,
  contactRoleSelectPrompt,
} from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useSendMatterEmail } from "@/lib/data/react-query/matters/matter-email";
import type {
  ConnectionContactPreview,
  Contact,
  MatterContactConnection,
  MatterFile,
} from "@/lib/types";
import {
  CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS,
} from "@/lib/utils/contact-role-select-card-class";
import {
  getMatterEmailRecipientContacts,
  resolveMatterEmailRecipient,
  toMatterEmailRecipient,
} from "@/lib/utils/matters/matter-email-recipients";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Paperclip, PlusCircle, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface SendMatterEmailSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  description?: string;
  matterId: string;
  contactId?: string;
  toEmails?: string[];
  templateName?: "CustomMessageEmailTemplate";
  templateData?: {
    subject?: string;
    custom_message?: string;
    firm_name?: string;
  };
  matterFiles?: string[];
  onSuccess?: () => void;
}

const formSchema = z.object({
  recipientContact: z.any().optional(),
  contactId: z.string().min(1, "Recipient contact is required"),
  emailId: z.string().min(1, "Recipient email is required"),
  subject: z.string().trim().min(1, "Subject is required"),
  customMessage: z.string().trim().min(1, "Message is required"),
});

type FormSchema = z.infer<typeof formSchema>;

export const SendMatterEmailSlideover = ({
  open,
  setOpen,
  title,
  description,
  matterId,
  contactId,
  toEmails,
  templateName = "CustomMessageEmailTemplate",
  templateData,
  matterFiles,
  onSuccess,
}: SendMatterEmailSlideoverProps) => {
  const formId = "send-matter-email-form";
  const { currentUser } = useUser();
  const {
    mutateAsync: sendMatterEmailMutation,
    isPending,
    isSuccess,
    reset: resetSendMatterEmail,
  } = useSendMatterEmail();

  const { data: matterResponse } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open && Boolean(matterId),
  });
  const [isSelectDocumentOpen, setIsSelectDocumentOpen] = useState(false);
  const [isChoosingContact, setIsChoosingContact] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);
  const hasInitializedRecipientRef = useRef(false);
  const hasExplicitRecipient = Boolean(contactId) || Boolean(toEmails?.length);
  const shouldStartWithContactSelection = !hasExplicitRecipient;

  const matter = matterResponse?.data;
  const initialMatterFileIds = useMemo(() => matterFiles ?? [], [matterFiles]);
  const recipientContacts = useMemo(
    () => getMatterEmailRecipientContacts(matter),
    [matter],
  );
  const explicitContactIsEmbedded = recipientContacts.some(
    (contact) => contact.contactId === contactId,
  );
  const explicitContactQuery = useQuery({
    ...retrieveContactQueryOptions(contactId),
    enabled: open && Boolean(contactId) && !explicitContactIsEmbedded,
  });
  const recipientContactsById = useMemo(() => {
    const map = new Map(
      recipientContacts.map((contact) => [contact.contactId, contact]),
    );
    const explicitContact = explicitContactQuery.data?.data;
    if (explicitContact && explicitContact.id === contactId) {
      map.set(
        explicitContact.id,
        toMatterEmailRecipient(
          explicitContact,
          map.get(explicitContact.id)?.role,
        ),
      );
    }
    return map;
  }, [recipientContacts, explicitContactQuery.data, contactId]);

  const resolveInitialRecipient = useMemo(
    () =>
      resolveMatterEmailRecipient(
        [...recipientContactsById.values()],
        contactId,
        toEmails,
      ),
    [contactId, recipientContactsById, toEmails],
  );

  const defaultValues = useMemo<FormSchema>(
    () => ({
      recipientContact: undefined,
      contactId: "",
      emailId: "",
      subject: templateData?.subject ?? "",
      customMessage: templateData?.custom_message ?? "",
    }),
    [templateData?.custom_message, templateData?.subject],
  );

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
      setIsChoosingContact(shouldStartWithContactSelection);
      hasInitializedRecipientRef.current = false;
    }
  }, [defaultValues, form, open, shouldStartWithContactSelection]);

  useEffect(() => {
    if (!open) return;
    if (hasInitializedRecipientRef.current) return;
    if (!matter) return;
    if (
      contactId &&
      !resolveInitialRecipient.recipientContact &&
      explicitContactQuery.isPending
    )
      return;
    form.setValue(
      "recipientContact",
      resolveInitialRecipient.recipientContact,
      {
        shouldDirty: false,
      },
    );
    form.setValue("contactId", resolveInitialRecipient.contactId, {
      shouldDirty: false,
    });
    form.setValue("emailId", resolveInitialRecipient.emailId, {
      shouldDirty: false,
    });
    hasInitializedRecipientRef.current = true;
  }, [
    form,
    open,
    resolveInitialRecipient,
    matter,
    contactId,
    explicitContactQuery.isPending,
  ]);

  useEffect(() => {
    if (!open || !matter) return;
    const nextSelectedFiles = initialMatterFileIds.length
      ? matter.files.filter((file) => initialMatterFileIds.includes(file.id))
      : [];
    setSelectedFiles(nextSelectedFiles);
  }, [initialMatterFileIds, matter, open]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      setOpen(false);
      form.reset(defaultValues);
      setSelectedFiles([]);
      resetSendMatterEmail();
    }
  }, [
    defaultValues,
    form,
    isSuccess,
    onSuccess,
    resetSendMatterEmail,
    setOpen,
  ]);

  const handleDocumentSelected = (files: MatterFile[]) => {
    if (files.length === 0) return;

    const uniqueFiles = Array.from(
      new Map(
        [...selectedFiles, ...files].map((file) => [file.id, file]),
      ).values(),
    );

    setSelectedFiles(uniqueFiles);
  };

  const handleRemoveDocument = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const selectedRecipientContact = form.watch("recipientContact") as
    | Contact
    | ConnectionContactPreview
    | undefined;
  const selectedContactId = form.watch("contactId");
  const selectedEmailId = form.watch("emailId");
  const selectedContactEmails = useMemo(
    () => selectedRecipientContact?.emails ?? [],
    [selectedRecipientContact],
  );
  const selectedContactEmailsKey = selectedContactEmails
    .map(
      (email) => `${email.id}:${email.address}:${email.isPrimary ? "1" : "0"}`,
    )
    .join("|");

  const selectedContactRole = selectedContactId
    ? recipientContactsById.get(selectedContactId)?.role
    : undefined;
  const handleEmailChange = (nextEmailId?: string) => {
    form.setValue("emailId", nextEmailId ?? "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const syncRecipientContact = (contact: Contact) => {
    hasInitializedRecipientRef.current = true;
    form.setValue("recipientContact", contact, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue("contactId", contact.id, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const nextEmails = contact.emails;
    const hasCurrentEmail = nextEmails.some(
      (email) => email.id === form.getValues("emailId"),
    );

    if (hasCurrentEmail) {
      return;
    }

    const primaryEmail =
      nextEmails.find((email) => email.isPrimary) ?? nextEmails.at(0);

    form.setValue("emailId", primaryEmail?.id ?? "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const clearRecipientContact = () => {
    form.setValue("recipientContact", undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue("contactId", "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue("emailId", "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!open) return;
    if (!selectedRecipientContact) return;

    const hasSelectedEmail = selectedContactEmails.some(
      (email) => email.id === selectedEmailId,
    );

    if (hasSelectedEmail) {
      return;
    }

    const nextEmailId =
      selectedContactEmails.find((email) => email.isPrimary)?.id ??
      selectedContactEmails.at(0)?.id ??
      "";

    form.setValue("emailId", nextEmailId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    form,
    open,
    selectedContactEmails,
    selectedEmailId,
    selectedRecipientContact,
  ]);

  const handleSubmit = async (data: FormSchema) => {
    const recipientEmail = selectedContactEmails.find(
      (email) => email.id === data.emailId,
    )?.address;
    if (!recipientEmail) return;

    await sendMatterEmailMutation({
      matterId,
      toEmails: [recipientEmail],
      templateName,
      templateData: {
        subject: data.subject,
        custom_message: data.customMessage,
        firm_name: currentUser.firm.name,
      },
      fileIds: selectedFiles.length
        ? selectedFiles.map((file) => file.id)
        : undefined,
    });
  };

  return (
    <Form {...form}>
      <AppModal
        title={isChoosingContact ? "Select contact" : title}
        description={
          isChoosingContact
            ? "Choose a contact to email for this matter."
            : description
        }
        open={open}
        onOpenChange={setOpen}
        size="xl"
        className={isChoosingContact ? "h-[min(80vh,780px)]" : undefined}
        bodyClassName={isChoosingContact ? "px-2 pt-3 pb-2" : undefined}
        bodyContained={isChoosingContact}
        loading={isPending}
        isDirty={form.formState.isDirty}
        footer={
          isChoosingContact ? (
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChoosingContact(false);
                }}
              >
                Back
              </Button>
            </div>
          ) : (
            <div className="flex w-full justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <FormSubmitButton
                disabled={false}
                loading={isPending}
                title="Send email"
                submitFormId={formId}
              />
            </div>
          )
        }
      >
        {isChoosingContact ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <AddContactFormField
              label="Contact"
              register={form.register("recipientContact")}
              matterId={matterId}
              inlineContactPicker
              parentSlideoverOpen={open}
              onContactSelectedForSubmit={(contact) => {
                syncRecipientContact(contact);
                setIsChoosingContact(false);
              }}
              className="flex min-h-0 min-w-0 flex-1 flex-col"
            />
          </div>
        ) : (
          <form
            id={formId}
            className="w-full min-w-0 space-y-4"
            onSubmit={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await form.handleSubmit(handleSubmit)();
            }}
          >
            <fieldset className="min-w-0 space-y-4" disabled={isPending}>
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <label className="text-sm font-medium">Contact</label>
                  {form.formState.errors.contactId ? (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.contactId.message}
                    </p>
                  ) : null}
                </div>
                {selectedRecipientContact ? (
                  <MatterContactConnectionModalPreview
                    contactId={selectedRecipientContact.id}
                    previewContact={selectedRecipientContact}
                    matterId={matterId}
                    matterContactConnection={
                      {
                        label: selectedContactRole ?? "Primary Contact",
                        contactId: selectedRecipientContact.id,
                      } as MatterContactConnection
                    }
                    onRemoveContact={clearRecipientContact}
                    onContactUpdated={syncRecipientContact}
                  />
                ) : (
                  <div className={CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS}>
                    <button
                      type="button"
                      className={CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS}
                      onClick={() => {
                        setIsChoosingContact(true);
                      }}
                      aria-label={contactRoleSelectPrompt("Contact")}
                    />
                    <div className={CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS}>
                      <div className={CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS}>
                        <span className="bg-muted text-muted-foreground flex h-7 w-7 items-center justify-center rounded-full">
                          <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                        </span>
                        <Typography className="text-foreground line-clamp-1 text-sm leading-tight font-medium">
                          {contactRoleSelectPrompt("Contact")}
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <SelectFormField
                  key={`${selectedContactId || "no-contact"}:${selectedContactEmailsKey}`}
                  label="Email"
                  register={form.register("emailId")}
                  items={selectedContactEmails}
                  itemsKey="id"
                  onChange={handleEmailChange}
                  placeholder={
                    selectedContactId ? "Select email" : "Select contact first"
                  }
                  formatItemLabel={(item) => item?.address ?? ""}
                  formatItemContext={(item) =>
                    item?.label
                      ? `${item.label}${item.isPrimary ? " · Primary" : ""}`
                      : item?.isPrimary
                        ? "Primary"
                        : undefined
                  }
                  formatSelectedItemLabel={(item) => item?.address ?? ""}
                  emptyMessage="No emails available for this contact"
                />
              </div>
              <div>
                <TextFormField
                  label="Subject"
                  register={form.register("subject")}
                  placeholder="Email subject"
                />
              </div>
              <div>
                <TextAreaFormField
                  label="Message"
                  register={form.register("customMessage")}
                  textAreaClassName="field-sizing-fixed min-h-40 min-w-0 max-w-full [overflow-wrap:anywhere]"
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <Typography size="sm" variant="subHeading">
                    Attachments
                  </Typography>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsSelectDocumentOpen(true);
                    }}
                  >
                    <Paperclip className="mr-2 h-4 w-4" />
                    {selectedFiles.length > 0 ? "Add files" : "Choose files"}
                  </Button>
                </div>
                <div className="bg-card rounded-md border p-3">
                  {selectedFiles.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedFiles.map((file) => (
                        <li
                          key={file.id}
                          className="flex items-center justify-between gap-3 text-sm"
                        >
                          <span className="min-w-0 truncate">{file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => {
                              handleRemoveDocument(file.id);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted-foreground text-sm">
                      No matter files selected.
                    </div>
                  )}
                </div>
              </div>
            </fieldset>
          </form>
        )}
      </AppModal>
      {matter ? (
        <SelectDocumentModal
          open={isSelectDocumentOpen}
          setOpen={setIsSelectDocumentOpen}
          matter={matter}
          onDocumentSelected={handleDocumentSelected}
          multiple
          title="Select files"
          description="Choose files to attach to this email"
          confirmActionLabel="Add files"
        />
      ) : null}
    </Form>
  );
};
